import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cloneDeep } from 'lodash';
import {
    Typography,
    Button,
} from '@mui/material';
import NumberInput from '../../../../../common/components/UI/Input/Number';
import DurationInput from '../Step2-BreakRuleSection/DurationInput';
import RadioButton from '../../../../../common/components/UI/RadioGroup/RadioButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { RollingPeriodUnit } from '../../../../Enums';
const DefaultFieldValidationErrors = Object.freeze({
    maxHoursRequired: false,
    maxHoursGreaterThanPeriod: false,
});

// Matches the server bound, MaxHoursInRollingPeriodRuleHelper.HOURS_IN_ROLLING_PERIOD_MAXIMUM,
// which refuses anything above two weeks of hours.
const MAX_HOURS_MAXIMUM = 14 * 24;
// A quarter hour, the smallest value DurationInput can produce and the server's
// HOURS_IN_ROLLING_PERIOD_MINIMUM. An empty field is still refused: 0 means unset, not a maximum of zero.
const MAX_HOURS_MINIMUM = 15 / 60;
const ROLLING_PERIOD_MAXIMUM_IN_DAYS = 31;
const ROLLING_PERIOD_MINIMUM_IN_DAYS = 1;
const ROLLING_PERIOD_MAXIMUM_IN_HOURS = ROLLING_PERIOD_MAXIMUM_IN_DAYS * 24;

/**
 * @param {{
 *  isDisabled: boolean,
 *  onAdd: Function,
 * }}
 */
export default function MaxHoursPerRollingPeriod(props) {
    const { isDisabled, onAdd } = props;
    const { t } = useTranslation('employmentBillboard', { keyPrefix: 'settings.ruleSet.scheduleRulesStep.rollingPeriodRules' });

    const [maxHoursInRollingPeriod, setMaxHoursInRollingPeriod] = useState(null);
    const [rollingPeriodNumber, setRollingPeriodNumber] = useState(1);
    const [rollingPeriodUnit, setRollingPeriodUnit] = useState(RollingPeriodUnit.Hours);
    const [fieldValidationErrors, setFieldValidationErrors] = React.useState(cloneDeep(DefaultFieldValidationErrors));
    const [maxHoursInputKey, setMaxHoursInputKey] = useState(0);

    const onChangeMaxHours = (value) => {
        // DurationInput caps the entry at MAX_HOURS_MAXIMUM; 00:00 clears the field so the placeholder comes back.
        const enteredValue = value || null;
        setMaxHoursInRollingPeriod(enteredValue);
        if (enteredValue !== value) {
            // DurationInput only refreshes its text when the bound value changes, so remount it when a zero entry was emptied.
            setMaxHoursInputKey((key) => key + 1);
        }
    }

    const resetValues = () => {
        setMaxHoursInRollingPeriod(null);
        setMaxHoursInputKey((key) => key + 1);
        setRollingPeriodNumber(1);
        setRollingPeriodUnit(RollingPeriodUnit.Hours);
        setFieldValidationErrors(cloneDeep(DefaultFieldValidationErrors));
    }
    
    const validateCurrentAwardingRule = () => {
        let isValid = true;
        const errors = cloneDeep(DefaultFieldValidationErrors);
        const rollingPeriodInHours = rollingPeriodUnit === RollingPeriodUnit.Hours ? rollingPeriodNumber : rollingPeriodNumber * 24;

        if (!maxHoursInRollingPeriod || maxHoursInRollingPeriod < MAX_HOURS_MINIMUM) {
            errors.maxHoursRequired = true;
            isValid = false;
        }
        else if (maxHoursInRollingPeriod > rollingPeriodInHours) {
            errors.maxHoursGreaterThanPeriod = true;
            isValid = false;
        }

        for (const property in errors){
            if (errors[property] !== fieldValidationErrors[property]){
                setFieldValidationErrors(errors);
            }
        }

        return isValid;
    }

    const handleAddRule = () => {
        if (validateCurrentAwardingRule()) {
            onAdd(maxHoursInRollingPeriod, rollingPeriodNumber, rollingPeriodUnit);
            resetValues();
        }
    };

    /**
     * @param {RollingPeriodUnit} unit 
     */
    const onChangeRollingPeriodUnit = (unit) => {
        setRollingPeriodUnit(unit);
        let rollingPeriodValue = 1;
        if (unit === RollingPeriodUnit.Hours){
            const valueInHours = rollingPeriodNumber * 24;
            rollingPeriodValue = Math.min(valueInHours, ROLLING_PERIOD_MAXIMUM_IN_HOURS);
        }
        else {
            const valueInDays = rollingPeriodNumber / 24;
            rollingPeriodValue = Math.max(valueInDays, ROLLING_PERIOD_MINIMUM_IN_DAYS)
        }
        setRollingPeriodNumber(rollingPeriodValue);
    }

    return (
        <>
            <div className="mhrp-number-input">
                <div style={{ marginRight: '16px' }}>
                    <DurationInput
                        key={maxHoursInputKey}
                        onChange={onChangeMaxHours}
                        value={maxHoursInRollingPeriod}
                        maxHours={MAX_HOURS_MAXIMUM}
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        disabled={isDisabled}
                        placeholder={t('placeholder')}
                        error={fieldValidationErrors.maxHoursRequired || fieldValidationErrors.maxHoursGreaterThanPeriod}
                    />
                </div>
                <Typography variant="body1">
                    {t('maxHours')}
                </Typography>
            </div>
            {
                fieldValidationErrors.maxHoursRequired ? (
                    <span style={{ color: 'red', display: 'block' }}>{t('errors.maxHoursRequired')}</span>) : null
            }
            {
                fieldValidationErrors.maxHoursGreaterThanPeriod ? (
                    <span style={{ color: 'red', display: 'block' }}>{t('errors.maxHoursGreaterThanPeriod')}</span>) : null
            }
            <div className="mhrp-rolling-period">
                <Typography variant="body1">
                    {t('rollingPeriod')}
                </Typography>
                <NumberInput
                    handleChangeInput={(value, target) => setRollingPeriodNumber(value)}
                    min={1}
                    max={rollingPeriodUnit === RollingPeriodUnit.Hours ? ROLLING_PERIOD_MAXIMUM_IN_HOURS : ROLLING_PERIOD_MAXIMUM_IN_DAYS}
                    step={1}
                    precision={0}
                    value={rollingPeriodNumber}
                    isDisabled={isDisabled}
                    strict={true}
                    target='rollingPeriodNumber'
                    error={false}
                />
                <RadioButton
                    radioGroupClass={'srs-timeUnitContainer'}
                    valueRadioGroup={rollingPeriodUnit !== null ? rollingPeriodUnit.toString() : null}
                    onChange={(event) => onChangeRollingPeriodUnit(parseInt(event.target.value))}
                    labelA={'Hours'}
                    valueA={RollingPeriodUnit.Hours.toString()}
                    labelB={'Days'}
                    valueB={RollingPeriodUnit.Days.toString()}
                    isRadioADisabled={isDisabled}
                    isRadioBDisabled={isDisabled}
                />
            </div>
            <div className="mhrp-buttons-container">
                <Button
                    color="primary"
                    variant="contained"
                    onClick={resetValues}
                    disabled={isDisabled}
                    className="srs--button"
                >
                    {t('clear')}
                </Button>
                <Button
                    color="primary"
                    variant="contained"
                    onClick={handleAddRule}
                    disabled={isDisabled}
                    className="srs--button"
                >
                    <>
                        <FontAwesomeIcon
                            className="mhrp-selection-add-update-btn"
                            icon={faPlus}
                        />
                        {t('addRule')}
                    </>
                </Button>
                </div>
        </>
    );
}